// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/*
 * AcademicOS Design System
 * ─────────────────────────────────────────────────────────────────────────────
 * Applies to all Studio pages. Covers:
 *   1. CSS custom properties (design tokens)
 *   2. WCAG 2.1 AA foundations (focus, contrast, motion, high-contrast)
 *   3. Professional SaaS typography and spacing
 *   4. Micro-interaction utilities
 */

/* ── 1. Design Tokens ────────────────────────────────────────────────────────*/

:root {
  /* Brand colours */
  --color-brand-50:   #eff6ff;
  --color-brand-100:  #dbeafe;
  --color-brand-200:  #bfdbfe;
  --color-brand-500:  #3b82f6;
  --color-brand-600:  #2563eb;
  --color-brand-700:  #1d4ed8;
  --color-brand-900:  #1e3a8a;

  /* Neutrals */
  --color-gray-50:   #f9fafb;
  --color-gray-100:  #f3f4f6;
  --color-gray-200:  #e5e7eb;
  --color-gray-300:  #d1d5db;
  --color-gray-400:  #9ca3af;
  --color-gray-500:  #6b7280;
  --color-gray-600:  #4b5563;
  --color-gray-700:  #374151;
  --color-gray-800:  #1f2937;
  --color-gray-900:  #111827;

  /* Semantic colours */
  --color-success:   #16a34a;
  --color-warning:   #d97706;
  --color-danger:    #dc2626;
  --color-info:      #0284c7;

  /* Surface */
  --surface-base:    #ffffff;
  --surface-muted:   var(--color-gray-50);
  --surface-raised:  #ffffff;
  --surface-overlay: rgba(0, 0, 0, 0.40);

  /* Typography */
  --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --font-mono: 'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace;

  --text-xs:   0.75rem;    /* 12px */
  --text-sm:   0.875rem;   /* 14px */
  --text-base: 1rem;       /* 16px */
  --text-lg:   1.125rem;   /* 18px */
  --text-xl:   1.25rem;    /* 20px */
  --text-2xl:  1.5rem;     /* 24px */
  --text-3xl:  1.875rem;   /* 30px */

  --font-normal:   400;
  --font-medium:   500;
  --font-semibold: 600;
  --font-bold:     700;

  --leading-tight:  1.25;
  --leading-normal: 1.5;
  --leading-relaxed: 1.625;

  /* Spacing (4px base unit) */
  --space-1:  0.25rem;   /* 4px */
  --space-2:  0.5rem;    /* 8px */
  --space-3:  0.75rem;   /* 12px */
  --space-4:  1rem;      /* 16px */
  --space-5:  1.25rem;   /* 20px */
  --space-6:  1.5rem;    /* 24px */
  --space-8:  2rem;      /* 32px */
  --space-10: 2.5rem;    /* 40px */
  --space-12: 3rem;      /* 48px */
  --space-16: 4rem;      /* 64px */

  /* Radius */
  --radius-sm: 0.25rem;
  --radius-md: 0.375rem;
  --radius-lg: 0.5rem;
  --radius-xl: 0.75rem;
  --radius-2xl: 1rem;
  --radius-full: 9999px;

  /* Shadows */
  --shadow-sm:  0 1px 2px 0 rgba(0,0,0,0.05);
  --shadow-md:  0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05);
  --shadow-lg:  0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.05);
  --shadow-xl:  0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.04);

  /* Borders */
  --border-color: var(--color-gray-200);
  --border-color-strong: var(--color-gray-300);

  /* Transitions */
  --transition-fast:   150ms cubic-bezier(0.16, 1, 0.3, 1);
  --transition-normal: 250ms cubic-bezier(0.16, 1, 0.3, 1);
  --transition-slow:   400ms cubic-bezier(0.16, 1, 0.3, 1);

  /* Focus ring — WCAG 2.4.7, 2.4.11 */
  --focus-ring: 0 0 0 3px rgba(59, 130, 246, 0.5);
  --focus-ring-offset: 0 0 0 2px #fff, 0 0 0 4px rgba(59, 130, 246, 0.6);

  /* Z-index scale */
  --z-base:    1;
  --z-dropdown: 100;
  --z-sticky:   200;
  --z-modal:    300;
  --z-toast:    400;
  --z-skipnav:  9999;
}


/* ── 2. WCAG Accessibility Foundations ───────────────────────────────────────*/

/* 2a. Focus styles — WCAG 2.4.7 (Focus Visible) ≥ AA, 2.4.11 (WCAG 2.2 Enhanced) */
*:focus-visible {
  outline: 3px solid var(--color-brand-500);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* Remove the default focus ring when focus-visible is supported */
*:focus:not(:focus-visible) {
  outline: none;
}

/* Interactive elements that need visible focus */
a:focus-visible,
button:focus-visible,
[role="button"]:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible,
[tabindex]:focus-visible {
  outline: 3px solid var(--color-brand-500);
  outline-offset: 2px;
  box-shadow: var(--focus-ring);
}

/* Bootstrap overrides — Bootstrap's .form-control focus style */
.form-control:focus,
.form-select:focus {
  border-color: var(--color-brand-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.btn:focus-visible,
.btn:focus {
  box-shadow: var(--focus-ring-offset) !important;
  outline: none !important;
}

.nav-link:focus-visible,
.dropdown-item:focus-visible {
  outline: 3px solid var(--color-brand-500);
  outline-offset: 1px;
}

/* 2b. Reduced motion — WCAG 2.3.3 (Animation from Interactions) */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }

  .accordion-collapse,
  .collapsing,
  .fade,
  .modal.fade .modal-dialog {
    transition: none !important;
  }
}

/* 2c. High contrast mode — WCAG 1.4.11 (Non-text Contrast) */
@media (forced-colors: active) {
  .btn,
  .badge,
  .card,
  .form-control,
  .form-select {
    border: 1px solid ButtonText;
  }

  *:focus-visible {
    outline: 3px solid Highlight;
  }
}

/* 2d. Print accessibility */
@media print {
  .no-print { display: none !important; }
  a[href]::after { content: ' (' attr(href) ')'; }
  .btn { display: none; }
}


/* ── 3. Skip Navigation — WCAG 2.4.1 (Bypass Blocks) ────────────────────────*/

.skip-nav {
  position: fixed;
  top: -100%;
  left: var(--space-4);
  z-index: var(--z-skipnav);
  padding: var(--space-3) var(--space-5);
  background: var(--color-brand-600);
  color: #fff;
  font-weight: var(--font-semibold);
  font-size: var(--text-sm);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  text-decoration: none;
  transition: top var(--transition-fast);
}

.skip-nav:focus {
  top: 0;
  outline: 3px solid #fff;
  outline-offset: 2px;
}


/* ── 4. Typography ───────────────────────────────────────────────────────────*/

body {
  font-family: var(--font-sans);
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--color-gray-800);
  background-color: var(--color-gray-50);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Heading scale */
h1, .h1 { font-size: var(--text-3xl); font-weight: var(--font-bold);     line-height: var(--leading-tight);  letter-spacing: -0.025em; }
h2, .h2 { font-size: var(--text-2xl); font-weight: var(--font-semibold);  line-height: var(--leading-tight);  letter-spacing: -0.02em; }
h3, .h3 { font-size: var(--text-xl);  font-weight: var(--font-semibold);  line-height: var(--leading-normal); }
h4, .h4 { font-size: var(--text-lg);  font-weight: var(--font-semibold);  line-height: var(--leading-normal); }
h5, .h5 { font-size: var(--text-base); font-weight: var(--font-semibold); line-height: var(--leading-normal); }
h6, .h6 { font-size: var(--text-sm);   font-weight: var(--font-semibold); line-height: var(--leading-normal); text-transform: uppercase; letter-spacing: 0.05em; color: var(--color-gray-500); }

code, pre { font-family: var(--font-mono); }


/* ── 5. Professional Card Pattern ────────────────────────────────────────────*/

.card {
  background: var(--surface-raised);
  border: 1px solid var(--border-color) !important;
  border-radius: var(--radius-xl) !important;
  box-shadow: var(--shadow-sm) !important;
  transition: box-shadow var(--transition-fast), border-color var(--transition-fast);
}

.card:hover {
  box-shadow: var(--shadow-md) !important;
}

.card-header {
  background: transparent !important;
  border-bottom: 1px solid var(--border-color) !important;
  padding: var(--space-4) var(--space-6) !important;
}

.card-body {
  padding: var(--space-6) !important;
}

/* Stat/metric card */
.stat-card {
  background: var(--surface-raised);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: var(--space-5) var(--space-6);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--transition-fast);
}

.stat-card:hover { box-shadow: var(--shadow-md); }

.stat-card__label {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-gray-500);
  margin-bottom: var(--space-1);
}

.stat-card__value {
  font-size: var(--text-3xl);
  font-weight: var(--font-bold);
  color: var(--color-gray-900);
  line-height: 1;
  margin-bottom: var(--space-1);
}

.stat-card__delta {
  font-size: var(--text-xs);
  color: var(--color-gray-500);
}


/* ── 6. Professional Table ───────────────────────────────────────────────────*/

.table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--text-sm);
  color: var(--color-gray-700);
}

.table thead th {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-gray-500);
  background: var(--color-gray-50);
  border-bottom: 1px solid var(--border-color);
  padding: var(--space-3) var(--space-4);
  white-space: nowrap;
}

.table tbody tr {
  transition: background var(--transition-fast);
}

.table tbody tr:hover {
  background: var(--color-brand-50);
}

.table tbody td {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.table tbody tr:last-child td { border-bottom: none; }


/* ── 7. Badge & Status ───────────────────────────────────────────────────────*/

.badge {
  font-weight: var(--font-medium);
  letter-spacing: 0.02em;
  padding: 0.2em 0.55em;
  border-radius: var(--radius-full);
}

/* Semantic status badges (text + colour, not colour-only — WCAG 1.4.1) */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  padding: 0.2em 0.6em;
  border-radius: var(--radius-full);
}

.status-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}


/* ── 8. Button Refinements ───────────────────────────────────────────────────*/

.btn {
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
  border-radius: var(--radius-lg);
  padding: 0.45rem 1rem;
  transition: all var(--transition-fast);
  line-height: 1.5;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.btn-primary {
  background: var(--color-brand-600);
  border-color: var(--color-brand-600);
}

.btn-primary:hover {
  background: var(--color-brand-700);
  border-color: var(--color-brand-700);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-primary:active { transform: translateY(0); }

.btn-icon {
  padding: 0.5rem;
  min-width: 2.25rem;
  justify-content: center;
}

/* Pill button style (common in nav/filter areas) */
.btn-pill {
  border-radius: var(--radius-full) !important;
  padding-left: var(--space-5) !important;
  padding-right: var(--space-5) !important;
}


/* ── 9. Form Improvements ────────────────────────────────────────────────────*/

.form-label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-gray-700);
  margin-bottom: var(--space-1);
}

.form-control,
.form-select {
  border-color: var(--border-color-strong);
  border-radius: var(--radius-lg);
  font-size: var(--text-sm);
  padding: var(--space-2) var(--space-3);
  color: var(--color-gray-800);
  background: var(--surface-raised);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.form-control::placeholder { color: var(--color-gray-400); }

.form-text {
  font-size: var(--text-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

/* Required field indicator — accessible (not colour-only) */
.required-label::after {
  content: ' *';
  color: var(--color-danger);
  font-weight: var(--font-bold);
  aria-hidden: true;
}


/* ── 10. Navigation & Sidebar ────────────────────────────────────────────────*/

.navbar {
  border-bottom: 1px solid var(--border-color);
  background: var(--surface-raised) !important;
  box-shadow: var(--shadow-sm);
  height: 56px;
}

.nav-tabs .nav-link {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-gray-500);
  border: none;
  border-bottom: 2px solid transparent;
  padding: var(--space-3) var(--space-4);
  border-radius: 0;
  transition: color var(--transition-fast), border-color var(--transition-fast);
}

.nav-tabs .nav-link:hover { color: var(--color-brand-600); border-bottom-color: var(--color-brand-200); }
.nav-tabs .nav-link.active {
  color: var(--color-brand-700) !important;
  border-bottom: 2px solid var(--color-brand-600) !important;
  background: transparent !important;
  font-weight: var(--font-semibold);
}


/* ── 11. Sidebar / Left-panel pattern ───────────────────────────────────────*/

.sidebar {
  background: var(--surface-raised);
  border-right: 1px solid var(--border-color);
  overflow-y: auto;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-gray-600);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
  margin-bottom: 2px;
}

.sidebar-item:hover { background: var(--color-gray-100); color: var(--color-gray-900); }
.sidebar-item--active { background: var(--color-brand-50); color: var(--color-brand-700); font-weight: var(--font-semibold); }


/* ── 12. Loading skeletons ───────────────────────────────────────────────────*/

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.skeleton {
  background: var(--color-gray-200);
  border-radius: var(--radius-md);
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .skeleton { animation: none; }
}


/* ── 13. Toast / Notification refinements ────────────────────────────────────*/

.alert {
  border-radius: var(--radius-lg);
  border-left-width: 4px !important;
  font-size: var(--text-sm);
}


/* ── Classroom Navbar — deep navy that wins over Bootstrap's bg-dark ─────────*/
.classroom-nav.navbar {
    background-color: #1a1f2e !important;
    border-bottom: 1px solid #2d3748 !important;
}

.classroom-nav .navbar-brand span,
.classroom-nav .nav-link,
.classroom-nav .navbar-text {
    color: #e2e8f0 !important;
}

.classroom-nav .nav-link:hover  { color: #ffffff !important; }

/* ── 14. Accordion / Collapsible ─────────────────────────────────────────────*/

.accordion-button {
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
  color: var(--color-gray-700);
}

.accordion-button:not(.collapsed) {
  color: var(--color-brand-700);
  background: var(--color-brand-50);
  box-shadow: none;
}


/* ── 15. Utility classes ─────────────────────────────────────────────────────*/

/* Visually hidden but accessible to screen readers — WCAG best practice */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Live region for dynamic announcements */
.live-region {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
}

/* Loading state with accessible announcement */
[aria-busy="true"] { cursor: wait; }

/* Divider */
.divider {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: var(--space-4) 0;
}

/* Section heading */
.section-label {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-gray-400);
  margin-bottom: var(--space-3);
}

/* Breadcrumb */
.breadcrumb {
  font-size: var(--text-sm);
  color: var(--color-gray-500);
}

.breadcrumb-item + .breadcrumb-item::before {
  content: '/';
  color: var(--color-gray-300);
}

/* Smooth page transitions */
.page-enter {
  opacity: 0;
  transform: translateY(4px);
}

.page-enter-active {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 200ms ease, transform 200ms ease;
}
`, "",{"version":3,"sources":["webpack://./src/styles/design-system.css"],"names":[],"mappings":"AAAA;;;;;;;;EAQE;;AAEF,gFAAgF;;AAEhF;EACE,kBAAkB;EAClB,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;;EAE3B,aAAa;EACb,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;;EAE1B,qBAAqB;EACrB,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;EAC1B,0BAA0B;;EAE1B,YAAY;EACZ,0BAA0B;EAC1B,uCAAuC;EACvC,0BAA0B;EAC1B,sCAAsC;;EAEtC,eAAe;EACf,+EAA+E;EAC/E,sEAAsE;;EAEtE,oBAAoB,KAAK,SAAS;EAClC,qBAAqB,IAAI,SAAS;EAClC,iBAAiB,QAAQ,SAAS;EAClC,qBAAqB,IAAI,SAAS;EAClC,oBAAoB,KAAK,SAAS;EAClC,mBAAmB,MAAM,SAAS;EAClC,qBAAqB,IAAI,SAAS;;EAElC,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;;EAEpB,sBAAsB;EACtB,qBAAqB;EACrB,wBAAwB;;EAExB,4BAA4B;EAC5B,mBAAmB,IAAI,QAAQ;EAC/B,kBAAkB,KAAK,QAAQ;EAC/B,mBAAmB,IAAI,SAAS;EAChC,gBAAgB,OAAO,SAAS;EAChC,mBAAmB,IAAI,SAAS;EAChC,kBAAkB,KAAK,SAAS;EAChC,gBAAgB,OAAO,SAAS;EAChC,kBAAkB,KAAK,SAAS;EAChC,gBAAgB,OAAO,SAAS;EAChC,gBAAgB,OAAO,SAAS;;EAEhC,WAAW;EACX,oBAAoB;EACpB,qBAAqB;EACrB,mBAAmB;EACnB,oBAAoB;EACpB,kBAAkB;EAClB,qBAAqB;;EAErB,YAAY;EACZ,0CAA0C;EAC1C,8EAA8E;EAC9E,gFAAgF;EAChF,iFAAiF;;EAEjF,YAAY;EACZ,qCAAqC;EACrC,4CAA4C;;EAE5C,gBAAgB;EAChB,wDAAwD;EACxD,wDAAwD;EACxD,wDAAwD;;EAExD,oCAAoC;EACpC,+CAA+C;EAC/C,sEAAsE;;EAEtE,kBAAkB;EAClB,cAAc;EACd,iBAAiB;EACjB,iBAAiB;EACjB,iBAAiB;EACjB,iBAAiB;EACjB,kBAAkB;AACpB;;;AAGA,gFAAgF;;AAEhF,mFAAmF;AACnF;EACE,yCAAyC;EACzC,mBAAmB;EACnB,+BAA+B;AACjC;;AAEA,kEAAkE;AAClE;EACE,aAAa;AACf;;AAEA,iDAAiD;AACjD;;;;;;;EAOE,yCAAyC;EACzC,mBAAmB;EACnB,6BAA6B;AAC/B;;AAEA,gEAAgE;AAChE;;EAEE,oCAAoC;EACpC,8CAA8C;EAC9C,aAAa;AACf;;AAEA;;EAEE,+CAA+C;EAC/C,wBAAwB;AAC1B;;AAEA;;EAEE,yCAAyC;EACzC,mBAAmB;AACrB;;AAEA,kEAAkE;AAClE;EACE;;;IAGE,qCAAqC;IACrC,uCAAuC;IACvC,sCAAsC;IACtC,gCAAgC;EAClC;;EAEA;;;;IAIE,2BAA2B;EAC7B;AACF;;AAEA,6DAA6D;AAC7D;EACE;;;;;IAKE,4BAA4B;EAC9B;;EAEA;IACE,4BAA4B;EAC9B;AACF;;AAEA,4BAA4B;AAC5B;EACE,YAAY,wBAAwB,EAAE;EACtC,iBAAiB,4BAA4B,EAAE;EAC/C,OAAO,aAAa,EAAE;AACxB;;;AAGA,+EAA+E;;AAE/E;EACE,eAAe;EACf,UAAU;EACV,oBAAoB;EACpB,yBAAyB;EACzB,sCAAsC;EACtC,kCAAkC;EAClC,WAAW;EACX,iCAAiC;EACjC,yBAAyB;EACzB,oDAAoD;EACpD,qBAAqB;EACrB,sCAAsC;AACxC;;AAEA;EACE,MAAM;EACN,uBAAuB;EACvB,mBAAmB;AACrB;;;AAGA,gFAAgF;;AAEhF;EACE,6BAA6B;EAC7B,2BAA2B;EAC3B,kCAAkC;EAClC,4BAA4B;EAC5B,sCAAsC;EACtC,mCAAmC;EACnC,kCAAkC;AACpC;;AAEA,kBAAkB;AAClB,UAAU,0BAA0B,EAAE,6BAA6B,MAAM,iCAAiC,GAAG,wBAAwB,EAAE;AACvI,UAAU,0BAA0B,EAAE,iCAAiC,GAAG,iCAAiC,GAAG,uBAAuB,EAAE;AACvI,UAAU,yBAAyB,GAAG,iCAAiC,GAAG,kCAAkC,EAAE;AAC9G,UAAU,yBAAyB,GAAG,iCAAiC,GAAG,kCAAkC,EAAE;AAC9G,UAAU,2BAA2B,EAAE,iCAAiC,EAAE,kCAAkC,EAAE;AAC9G,UAAU,yBAAyB,IAAI,iCAAiC,EAAE,kCAAkC,EAAE,yBAAyB,EAAE,sBAAsB,EAAE,4BAA4B,EAAE;;AAE/L,YAAY,6BAA6B,EAAE;;;AAG3C,gFAAgF;;AAEhF;EACE,iCAAiC;EACjC,gDAAgD;EAChD,0CAA0C;EAC1C,uCAAuC;EACvC,kFAAkF;AACpF;;AAEA;EACE,uCAAuC;AACzC;;AAEA;EACE,kCAAkC;EAClC,uDAAuD;EACvD,iDAAiD;AACnD;;AAEA;EACE,kCAAkC;AACpC;;AAEA,qBAAqB;AACrB;EACE,iCAAiC;EACjC,qCAAqC;EACrC,+BAA+B;EAC/B,sCAAsC;EACtC,4BAA4B;EAC5B,6CAA6C;AAC/C;;AAEA,mBAAmB,4BAA4B,EAAE;;AAEjD;EACE,yBAAyB;EACzB,iCAAiC;EACjC,yBAAyB;EACzB,sBAAsB;EACtB,4BAA4B;EAC5B,6BAA6B;AAC/B;;AAEA;EACE,0BAA0B;EAC1B,6BAA6B;EAC7B,4BAA4B;EAC5B,cAAc;EACd,6BAA6B;AAC/B;;AAEA;EACE,yBAAyB;EACzB,4BAA4B;AAC9B;;;AAGA,gFAAgF;;AAEhF;EACE,yBAAyB;EACzB,iBAAiB;EACjB,yBAAyB;EACzB,4BAA4B;AAC9B;;AAEA;EACE,yBAAyB;EACzB,iCAAiC;EACjC,yBAAyB;EACzB,sBAAsB;EACtB,4BAA4B;EAC5B,gCAAgC;EAChC,4CAA4C;EAC5C,sCAAsC;EACtC,mBAAmB;AACrB;;AAEA;EACE,6CAA6C;AAC/C;;AAEA;EACE,iCAAiC;AACnC;;AAEA;EACE,sCAAsC;EACtC,4CAA4C;EAC5C,sBAAsB;AACxB;;AAEA,gCAAgC,mBAAmB,EAAE;;;AAGrD,gFAAgF;;AAEhF;EACE,+BAA+B;EAC/B,sBAAsB;EACtB,qBAAqB;EACrB,iCAAiC;AACnC;;AAEA,yEAAyE;AACzE;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,yBAAyB;EACzB,+BAA+B;EAC/B,oBAAoB;EACpB,iCAAiC;AACnC;;AAEA;EACE,WAAW;EACX,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,wBAAwB;EACxB,cAAc;AAChB;;;AAGA,gFAAgF;;AAEhF;EACE,+BAA+B;EAC/B,yBAAyB;EACzB,+BAA+B;EAC/B,qBAAqB;EACrB,sCAAsC;EACtC,gBAAgB;EAChB,oBAAoB;EACpB,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,kCAAkC;EAClC,oCAAoC;AACtC;;AAEA;EACE,kCAAkC;EAClC,oCAAoC;EACpC,2BAA2B;EAC3B,4BAA4B;AAC9B;;AAEA,sBAAsB,wBAAwB,EAAE;;AAEhD;EACE,eAAe;EACf,kBAAkB;EAClB,uBAAuB;AACzB;;AAEA,mDAAmD;AACnD;EACE,4CAA4C;EAC5C,uCAAuC;EACvC,wCAAwC;AAC1C;;;AAGA,gFAAgF;;AAEhF;EACE,yBAAyB;EACzB,+BAA+B;EAC/B,4BAA4B;EAC5B,6BAA6B;AAC/B;;AAEA;;EAEE,wCAAwC;EACxC,+BAA+B;EAC/B,yBAAyB;EACzB,sCAAsC;EACtC,4BAA4B;EAC5B,iCAAiC;EACjC,kFAAkF;AACpF;;AAEA,6BAA6B,4BAA4B,EAAE;;AAE3D;EACE,yBAAyB;EACzB,4BAA4B;EAC5B,0BAA0B;AAC5B;;AAEA,4DAA4D;AAC5D;EACE,aAAa;EACb,0BAA0B;EAC1B,6BAA6B;EAC7B,iBAAiB;AACnB;;;AAGA,gFAAgF;;AAEhF;EACE,4CAA4C;EAC5C,4CAA4C;EAC5C,4BAA4B;EAC5B,YAAY;AACd;;AAEA;EACE,yBAAyB;EACzB,+BAA+B;EAC/B,4BAA4B;EAC5B,YAAY;EACZ,oCAAoC;EACpC,sCAAsC;EACtC,gBAAgB;EAChB,6EAA6E;AAC/E;;AAEA,4BAA4B,6BAA6B,EAAE,2CAA2C,EAAE;AACxG;EACE,wCAAwC;EACxC,0DAA0D;EAC1D,kCAAkC;EAClC,iCAAiC;AACnC;;;AAGA,+EAA+E;;AAE/E;EACE,iCAAiC;EACjC,2CAA2C;EAC3C,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,sCAAsC;EACtC,yBAAyB;EACzB,+BAA+B;EAC/B,4BAA4B;EAC5B,+BAA+B;EAC/B,eAAe;EACf,2EAA2E;EAC3E,kBAAkB;AACpB;;AAEA,sBAAsB,iCAAiC,EAAE,4BAA4B,EAAE;AACvF,wBAAwB,iCAAiC,EAAE,6BAA6B,EAAE,iCAAiC,EAAE;;;AAG7H,gFAAgF;;AAEhF;EACE,WAAW,UAAU,EAAE;EACvB,MAAM,YAAY,EAAE;AACtB;;AAEA;EACE,iCAAiC;EACjC,+BAA+B;EAC/B,mDAAmD;AACrD;;AAEA;EACE,YAAY,eAAe,EAAE;AAC/B;;;AAGA,gFAAgF;;AAEhF;EACE,+BAA+B;EAC/B,iCAAiC;EACjC,yBAAyB;AAC3B;;;AAGA,gFAAgF;AAChF;IACI,oCAAoC;IACpC,2CAA2C;AAC/C;;AAEA;;;IAGI,yBAAyB;AAC7B;;AAEA,kCAAkC,yBAAyB,EAAE;;AAE7D,gFAAgF;;AAEhF;EACE,+BAA+B;EAC/B,yBAAyB;EACzB,4BAA4B;AAC9B;;AAEA;EACE,6BAA6B;EAC7B,iCAAiC;EACjC,gBAAgB;AAClB;;;AAGA,gFAAgF;;AAEhF,0EAA0E;AAC1E;EACE,kBAAkB;EAClB,UAAU;EACV,WAAW;EACX,UAAU;EACV,YAAY;EACZ,gBAAgB;EAChB,sBAAsB;EACtB,mBAAmB;EACnB,eAAe;AACjB;;AAEA,0CAA0C;AAC1C;EACE,kBAAkB;EAClB,UAAU;EACV,WAAW;EACX,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA,+CAA+C;AAC/C,qBAAqB,YAAY,EAAE;;AAEnC,YAAY;AACZ;EACE,YAAY;EACZ,yCAAyC;EACzC,wBAAwB;AAC1B;;AAEA,oBAAoB;AACpB;EACE,yBAAyB;EACzB,iCAAiC;EACjC,yBAAyB;EACzB,sBAAsB;EACtB,4BAA4B;EAC5B,6BAA6B;AAC/B;;AAEA,eAAe;AACf;EACE,yBAAyB;EACzB,4BAA4B;AAC9B;;AAEA;EACE,YAAY;EACZ,4BAA4B;AAC9B;;AAEA,4BAA4B;AAC5B;EACE,UAAU;EACV,0BAA0B;AAC5B;;AAEA;EACE,UAAU;EACV,wBAAwB;EACxB,oDAAoD;AACtD","sourcesContent":["/*\n * AcademicOS Design System\n * ─────────────────────────────────────────────────────────────────────────────\n * Applies to all Studio pages. Covers:\n *   1. CSS custom properties (design tokens)\n *   2. WCAG 2.1 AA foundations (focus, contrast, motion, high-contrast)\n *   3. Professional SaaS typography and spacing\n *   4. Micro-interaction utilities\n */\n\n/* ── 1. Design Tokens ────────────────────────────────────────────────────────*/\n\n:root {\n  /* Brand colours */\n  --color-brand-50:   #eff6ff;\n  --color-brand-100:  #dbeafe;\n  --color-brand-200:  #bfdbfe;\n  --color-brand-500:  #3b82f6;\n  --color-brand-600:  #2563eb;\n  --color-brand-700:  #1d4ed8;\n  --color-brand-900:  #1e3a8a;\n\n  /* Neutrals */\n  --color-gray-50:   #f9fafb;\n  --color-gray-100:  #f3f4f6;\n  --color-gray-200:  #e5e7eb;\n  --color-gray-300:  #d1d5db;\n  --color-gray-400:  #9ca3af;\n  --color-gray-500:  #6b7280;\n  --color-gray-600:  #4b5563;\n  --color-gray-700:  #374151;\n  --color-gray-800:  #1f2937;\n  --color-gray-900:  #111827;\n\n  /* Semantic colours */\n  --color-success:   #16a34a;\n  --color-warning:   #d97706;\n  --color-danger:    #dc2626;\n  --color-info:      #0284c7;\n\n  /* Surface */\n  --surface-base:    #ffffff;\n  --surface-muted:   var(--color-gray-50);\n  --surface-raised:  #ffffff;\n  --surface-overlay: rgba(0, 0, 0, 0.40);\n\n  /* Typography */\n  --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;\n  --font-mono: 'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace;\n\n  --text-xs:   0.75rem;    /* 12px */\n  --text-sm:   0.875rem;   /* 14px */\n  --text-base: 1rem;       /* 16px */\n  --text-lg:   1.125rem;   /* 18px */\n  --text-xl:   1.25rem;    /* 20px */\n  --text-2xl:  1.5rem;     /* 24px */\n  --text-3xl:  1.875rem;   /* 30px */\n\n  --font-normal:   400;\n  --font-medium:   500;\n  --font-semibold: 600;\n  --font-bold:     700;\n\n  --leading-tight:  1.25;\n  --leading-normal: 1.5;\n  --leading-relaxed: 1.625;\n\n  /* Spacing (4px base unit) */\n  --space-1:  0.25rem;   /* 4px */\n  --space-2:  0.5rem;    /* 8px */\n  --space-3:  0.75rem;   /* 12px */\n  --space-4:  1rem;      /* 16px */\n  --space-5:  1.25rem;   /* 20px */\n  --space-6:  1.5rem;    /* 24px */\n  --space-8:  2rem;      /* 32px */\n  --space-10: 2.5rem;    /* 40px */\n  --space-12: 3rem;      /* 48px */\n  --space-16: 4rem;      /* 64px */\n\n  /* Radius */\n  --radius-sm: 0.25rem;\n  --radius-md: 0.375rem;\n  --radius-lg: 0.5rem;\n  --radius-xl: 0.75rem;\n  --radius-2xl: 1rem;\n  --radius-full: 9999px;\n\n  /* Shadows */\n  --shadow-sm:  0 1px 2px 0 rgba(0,0,0,0.05);\n  --shadow-md:  0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05);\n  --shadow-lg:  0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.05);\n  --shadow-xl:  0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.04);\n\n  /* Borders */\n  --border-color: var(--color-gray-200);\n  --border-color-strong: var(--color-gray-300);\n\n  /* Transitions */\n  --transition-fast:   150ms cubic-bezier(0.16, 1, 0.3, 1);\n  --transition-normal: 250ms cubic-bezier(0.16, 1, 0.3, 1);\n  --transition-slow:   400ms cubic-bezier(0.16, 1, 0.3, 1);\n\n  /* Focus ring — WCAG 2.4.7, 2.4.11 */\n  --focus-ring: 0 0 0 3px rgba(59, 130, 246, 0.5);\n  --focus-ring-offset: 0 0 0 2px #fff, 0 0 0 4px rgba(59, 130, 246, 0.6);\n\n  /* Z-index scale */\n  --z-base:    1;\n  --z-dropdown: 100;\n  --z-sticky:   200;\n  --z-modal:    300;\n  --z-toast:    400;\n  --z-skipnav:  9999;\n}\n\n\n/* ── 2. WCAG Accessibility Foundations ───────────────────────────────────────*/\n\n/* 2a. Focus styles — WCAG 2.4.7 (Focus Visible) ≥ AA, 2.4.11 (WCAG 2.2 Enhanced) */\n*:focus-visible {\n  outline: 3px solid var(--color-brand-500);\n  outline-offset: 2px;\n  border-radius: var(--radius-sm);\n}\n\n/* Remove the default focus ring when focus-visible is supported */\n*:focus:not(:focus-visible) {\n  outline: none;\n}\n\n/* Interactive elements that need visible focus */\na:focus-visible,\nbutton:focus-visible,\n[role=\"button\"]:focus-visible,\ninput:focus-visible,\nselect:focus-visible,\ntextarea:focus-visible,\n[tabindex]:focus-visible {\n  outline: 3px solid var(--color-brand-500);\n  outline-offset: 2px;\n  box-shadow: var(--focus-ring);\n}\n\n/* Bootstrap overrides — Bootstrap's .form-control focus style */\n.form-control:focus,\n.form-select:focus {\n  border-color: var(--color-brand-500);\n  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);\n  outline: none;\n}\n\n.btn:focus-visible,\n.btn:focus {\n  box-shadow: var(--focus-ring-offset) !important;\n  outline: none !important;\n}\n\n.nav-link:focus-visible,\n.dropdown-item:focus-visible {\n  outline: 3px solid var(--color-brand-500);\n  outline-offset: 1px;\n}\n\n/* 2b. Reduced motion — WCAG 2.3.3 (Animation from Interactions) */\n@media (prefers-reduced-motion: reduce) {\n  *,\n  *::before,\n  *::after {\n    animation-duration: 0.01ms !important;\n    animation-iteration-count: 1 !important;\n    transition-duration: 0.01ms !important;\n    scroll-behavior: auto !important;\n  }\n\n  .accordion-collapse,\n  .collapsing,\n  .fade,\n  .modal.fade .modal-dialog {\n    transition: none !important;\n  }\n}\n\n/* 2c. High contrast mode — WCAG 1.4.11 (Non-text Contrast) */\n@media (forced-colors: active) {\n  .btn,\n  .badge,\n  .card,\n  .form-control,\n  .form-select {\n    border: 1px solid ButtonText;\n  }\n\n  *:focus-visible {\n    outline: 3px solid Highlight;\n  }\n}\n\n/* 2d. Print accessibility */\n@media print {\n  .no-print { display: none !important; }\n  a[href]::after { content: ' (' attr(href) ')'; }\n  .btn { display: none; }\n}\n\n\n/* ── 3. Skip Navigation — WCAG 2.4.1 (Bypass Blocks) ────────────────────────*/\n\n.skip-nav {\n  position: fixed;\n  top: -100%;\n  left: var(--space-4);\n  z-index: var(--z-skipnav);\n  padding: var(--space-3) var(--space-5);\n  background: var(--color-brand-600);\n  color: #fff;\n  font-weight: var(--font-semibold);\n  font-size: var(--text-sm);\n  border-radius: 0 0 var(--radius-lg) var(--radius-lg);\n  text-decoration: none;\n  transition: top var(--transition-fast);\n}\n\n.skip-nav:focus {\n  top: 0;\n  outline: 3px solid #fff;\n  outline-offset: 2px;\n}\n\n\n/* ── 4. Typography ───────────────────────────────────────────────────────────*/\n\nbody {\n  font-family: var(--font-sans);\n  font-size: var(--text-base);\n  line-height: var(--leading-normal);\n  color: var(--color-gray-800);\n  background-color: var(--color-gray-50);\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n}\n\n/* Heading scale */\nh1, .h1 { font-size: var(--text-3xl); font-weight: var(--font-bold);     line-height: var(--leading-tight);  letter-spacing: -0.025em; }\nh2, .h2 { font-size: var(--text-2xl); font-weight: var(--font-semibold);  line-height: var(--leading-tight);  letter-spacing: -0.02em; }\nh3, .h3 { font-size: var(--text-xl);  font-weight: var(--font-semibold);  line-height: var(--leading-normal); }\nh4, .h4 { font-size: var(--text-lg);  font-weight: var(--font-semibold);  line-height: var(--leading-normal); }\nh5, .h5 { font-size: var(--text-base); font-weight: var(--font-semibold); line-height: var(--leading-normal); }\nh6, .h6 { font-size: var(--text-sm);   font-weight: var(--font-semibold); line-height: var(--leading-normal); text-transform: uppercase; letter-spacing: 0.05em; color: var(--color-gray-500); }\n\ncode, pre { font-family: var(--font-mono); }\n\n\n/* ── 5. Professional Card Pattern ────────────────────────────────────────────*/\n\n.card {\n  background: var(--surface-raised);\n  border: 1px solid var(--border-color) !important;\n  border-radius: var(--radius-xl) !important;\n  box-shadow: var(--shadow-sm) !important;\n  transition: box-shadow var(--transition-fast), border-color var(--transition-fast);\n}\n\n.card:hover {\n  box-shadow: var(--shadow-md) !important;\n}\n\n.card-header {\n  background: transparent !important;\n  border-bottom: 1px solid var(--border-color) !important;\n  padding: var(--space-4) var(--space-6) !important;\n}\n\n.card-body {\n  padding: var(--space-6) !important;\n}\n\n/* Stat/metric card */\n.stat-card {\n  background: var(--surface-raised);\n  border: 1px solid var(--border-color);\n  border-radius: var(--radius-xl);\n  padding: var(--space-5) var(--space-6);\n  box-shadow: var(--shadow-sm);\n  transition: box-shadow var(--transition-fast);\n}\n\n.stat-card:hover { box-shadow: var(--shadow-md); }\n\n.stat-card__label {\n  font-size: var(--text-xs);\n  font-weight: var(--font-semibold);\n  text-transform: uppercase;\n  letter-spacing: 0.06em;\n  color: var(--color-gray-500);\n  margin-bottom: var(--space-1);\n}\n\n.stat-card__value {\n  font-size: var(--text-3xl);\n  font-weight: var(--font-bold);\n  color: var(--color-gray-900);\n  line-height: 1;\n  margin-bottom: var(--space-1);\n}\n\n.stat-card__delta {\n  font-size: var(--text-xs);\n  color: var(--color-gray-500);\n}\n\n\n/* ── 6. Professional Table ───────────────────────────────────────────────────*/\n\n.table {\n  border-collapse: separate;\n  border-spacing: 0;\n  font-size: var(--text-sm);\n  color: var(--color-gray-700);\n}\n\n.table thead th {\n  font-size: var(--text-xs);\n  font-weight: var(--font-semibold);\n  text-transform: uppercase;\n  letter-spacing: 0.06em;\n  color: var(--color-gray-500);\n  background: var(--color-gray-50);\n  border-bottom: 1px solid var(--border-color);\n  padding: var(--space-3) var(--space-4);\n  white-space: nowrap;\n}\n\n.table tbody tr {\n  transition: background var(--transition-fast);\n}\n\n.table tbody tr:hover {\n  background: var(--color-brand-50);\n}\n\n.table tbody td {\n  padding: var(--space-3) var(--space-4);\n  border-bottom: 1px solid var(--border-color);\n  vertical-align: middle;\n}\n\n.table tbody tr:last-child td { border-bottom: none; }\n\n\n/* ── 7. Badge & Status ───────────────────────────────────────────────────────*/\n\n.badge {\n  font-weight: var(--font-medium);\n  letter-spacing: 0.02em;\n  padding: 0.2em 0.55em;\n  border-radius: var(--radius-full);\n}\n\n/* Semantic status badges (text + colour, not colour-only — WCAG 1.4.1) */\n.status-badge {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.35em;\n  font-size: var(--text-xs);\n  font-weight: var(--font-medium);\n  padding: 0.2em 0.6em;\n  border-radius: var(--radius-full);\n}\n\n.status-badge::before {\n  content: '';\n  width: 6px;\n  height: 6px;\n  border-radius: 50%;\n  background: currentColor;\n  flex-shrink: 0;\n}\n\n\n/* ── 8. Button Refinements ───────────────────────────────────────────────────*/\n\n.btn {\n  font-weight: var(--font-medium);\n  font-size: var(--text-sm);\n  border-radius: var(--radius-lg);\n  padding: 0.45rem 1rem;\n  transition: all var(--transition-fast);\n  line-height: 1.5;\n  display: inline-flex;\n  align-items: center;\n  gap: var(--space-2);\n}\n\n.btn-primary {\n  background: var(--color-brand-600);\n  border-color: var(--color-brand-600);\n}\n\n.btn-primary:hover {\n  background: var(--color-brand-700);\n  border-color: var(--color-brand-700);\n  transform: translateY(-1px);\n  box-shadow: var(--shadow-md);\n}\n\n.btn-primary:active { transform: translateY(0); }\n\n.btn-icon {\n  padding: 0.5rem;\n  min-width: 2.25rem;\n  justify-content: center;\n}\n\n/* Pill button style (common in nav/filter areas) */\n.btn-pill {\n  border-radius: var(--radius-full) !important;\n  padding-left: var(--space-5) !important;\n  padding-right: var(--space-5) !important;\n}\n\n\n/* ── 9. Form Improvements ────────────────────────────────────────────────────*/\n\n.form-label {\n  font-size: var(--text-sm);\n  font-weight: var(--font-medium);\n  color: var(--color-gray-700);\n  margin-bottom: var(--space-1);\n}\n\n.form-control,\n.form-select {\n  border-color: var(--border-color-strong);\n  border-radius: var(--radius-lg);\n  font-size: var(--text-sm);\n  padding: var(--space-2) var(--space-3);\n  color: var(--color-gray-800);\n  background: var(--surface-raised);\n  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);\n}\n\n.form-control::placeholder { color: var(--color-gray-400); }\n\n.form-text {\n  font-size: var(--text-xs);\n  color: var(--color-gray-500);\n  margin-top: var(--space-1);\n}\n\n/* Required field indicator — accessible (not colour-only) */\n.required-label::after {\n  content: ' *';\n  color: var(--color-danger);\n  font-weight: var(--font-bold);\n  aria-hidden: true;\n}\n\n\n/* ── 10. Navigation & Sidebar ────────────────────────────────────────────────*/\n\n.navbar {\n  border-bottom: 1px solid var(--border-color);\n  background: var(--surface-raised) !important;\n  box-shadow: var(--shadow-sm);\n  height: 56px;\n}\n\n.nav-tabs .nav-link {\n  font-size: var(--text-sm);\n  font-weight: var(--font-medium);\n  color: var(--color-gray-500);\n  border: none;\n  border-bottom: 2px solid transparent;\n  padding: var(--space-3) var(--space-4);\n  border-radius: 0;\n  transition: color var(--transition-fast), border-color var(--transition-fast);\n}\n\n.nav-tabs .nav-link:hover { color: var(--color-brand-600); border-bottom-color: var(--color-brand-200); }\n.nav-tabs .nav-link.active {\n  color: var(--color-brand-700) !important;\n  border-bottom: 2px solid var(--color-brand-600) !important;\n  background: transparent !important;\n  font-weight: var(--font-semibold);\n}\n\n\n/* ── 11. Sidebar / Left-panel pattern ───────────────────────────────────────*/\n\n.sidebar {\n  background: var(--surface-raised);\n  border-right: 1px solid var(--border-color);\n  overflow-y: auto;\n}\n\n.sidebar-item {\n  display: flex;\n  align-items: center;\n  gap: var(--space-3);\n  padding: var(--space-2) var(--space-4);\n  font-size: var(--text-sm);\n  font-weight: var(--font-medium);\n  color: var(--color-gray-600);\n  border-radius: var(--radius-lg);\n  cursor: pointer;\n  transition: background var(--transition-fast), color var(--transition-fast);\n  margin-bottom: 2px;\n}\n\n.sidebar-item:hover { background: var(--color-gray-100); color: var(--color-gray-900); }\n.sidebar-item--active { background: var(--color-brand-50); color: var(--color-brand-700); font-weight: var(--font-semibold); }\n\n\n/* ── 12. Loading skeletons ───────────────────────────────────────────────────*/\n\n@keyframes skeleton-pulse {\n  0%, 100% { opacity: 1; }\n  50% { opacity: 0.4; }\n}\n\n.skeleton {\n  background: var(--color-gray-200);\n  border-radius: var(--radius-md);\n  animation: skeleton-pulse 1.5s ease-in-out infinite;\n}\n\n@media (prefers-reduced-motion: reduce) {\n  .skeleton { animation: none; }\n}\n\n\n/* ── 13. Toast / Notification refinements ────────────────────────────────────*/\n\n.alert {\n  border-radius: var(--radius-lg);\n  border-left-width: 4px !important;\n  font-size: var(--text-sm);\n}\n\n\n/* ── Classroom Navbar — deep navy that wins over Bootstrap's bg-dark ─────────*/\n.classroom-nav.navbar {\n    background-color: #1a1f2e !important;\n    border-bottom: 1px solid #2d3748 !important;\n}\n\n.classroom-nav .navbar-brand span,\n.classroom-nav .nav-link,\n.classroom-nav .navbar-text {\n    color: #e2e8f0 !important;\n}\n\n.classroom-nav .nav-link:hover  { color: #ffffff !important; }\n\n/* ── 14. Accordion / Collapsible ─────────────────────────────────────────────*/\n\n.accordion-button {\n  font-weight: var(--font-medium);\n  font-size: var(--text-sm);\n  color: var(--color-gray-700);\n}\n\n.accordion-button:not(.collapsed) {\n  color: var(--color-brand-700);\n  background: var(--color-brand-50);\n  box-shadow: none;\n}\n\n\n/* ── 15. Utility classes ─────────────────────────────────────────────────────*/\n\n/* Visually hidden but accessible to screen readers — WCAG best practice */\n.sr-only {\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border-width: 0;\n}\n\n/* Live region for dynamic announcements */\n.live-region {\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  overflow: hidden;\n  clip: rect(0,0,0,0);\n}\n\n/* Loading state with accessible announcement */\n[aria-busy=\"true\"] { cursor: wait; }\n\n/* Divider */\n.divider {\n  border: none;\n  border-top: 1px solid var(--border-color);\n  margin: var(--space-4) 0;\n}\n\n/* Section heading */\n.section-label {\n  font-size: var(--text-xs);\n  font-weight: var(--font-semibold);\n  text-transform: uppercase;\n  letter-spacing: 0.08em;\n  color: var(--color-gray-400);\n  margin-bottom: var(--space-3);\n}\n\n/* Breadcrumb */\n.breadcrumb {\n  font-size: var(--text-sm);\n  color: var(--color-gray-500);\n}\n\n.breadcrumb-item + .breadcrumb-item::before {\n  content: '/';\n  color: var(--color-gray-300);\n}\n\n/* Smooth page transitions */\n.page-enter {\n  opacity: 0;\n  transform: translateY(4px);\n}\n\n.page-enter-active {\n  opacity: 1;\n  transform: translateY(0);\n  transition: opacity 200ms ease, transform 200ms ease;\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
